/* eslint-disable max-lines */
import { DragDropContext } from '@hello-pangea/dnd';
import { FormContainer } from '@strata/react-form-container';
import { FormSection } from '@strata/react-form-section';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik, getIn, setIn, validateYupSchema, yupToFormErrors } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { DeleteItemDialog } from 'components/valueCase/DeleteItemDialog';
import { DynamicCards } from 'components/valueCase/DynamicCards';
import { EditConditionDialog } from 'components/valueCase/EditConditionDialog';
import { EditSubTaskDialog } from 'components/valueCase/EditSubTaskDialog';
import { compareProp, setSupplierNpiOptions } from 'components/valueCase/helpers';
import { PerformancePreview } from 'components/valueCase/PerformancePreview';
import { SelectExistingRequirementDialog } from 'components/valueCase/SelectExistingRequirementDialog';
import { TaskDetails } from 'components/valueCase/TaskDetails';
import { ValueCaseDetails } from 'components/valueCase/ValueCaseDetails';
import { valueCaseValidationSchema } from 'pages/valueCase/helpers/valueCaseValidationSchema';
import { handleError } from 'services/ErrorManagement';
import { get, post, put, useGet } from 'services/Http';
import { UnsavedChangesPrompt } from 'services/UnsavedChangesPrompt';

const getValueCase = async (valueCaseId) => get(`/performance/admin/value_cases/${valueCaseId}`);
const putValueCase = async (valueCaseId, valueCase) =>
    put(`/performance/admin/value_cases/${valueCaseId}`, valueCase);
const postValueCase = async (valueCase) => post('/performance/admin/value_cases', valueCase);

const defaultEndDate = '2100-01-01';

export const EditValueCase = () => {
    const stratRules = useGet('/performance/admin/strat_rules');
    const supplierNpis = useGet('/reference/suppliers/');
    const conditionBlueprints = useGet('/performance/conditions/blueprints');
    const { valueCaseId } = useParams();
    const navigate = useNavigate();
    const [openEditSubTaskDialog, setOpenEditSubTaskDialog] = useState();
    const [openEditConditionDialog, setOpenEditConditionDialog] = useState();
    const [openSubTaskPreview, setOpenSubTaskPreview] = useState();
    const [openSelectExistingDialog, setOpenSelectExistingDialog] = useState();
    const [openDeleteDialog, setOpenDeleteDialog] = useState();
    const [stratRuleConfigurableParams, setStratRuleConfigurableParams] = useState({});

    const [initialValues, setInitalValues] = useState({
        end_date: defaultEndDate,
        diagnoses: [],
        strat_rule_params: {},
        incentive_amt: 0,
        tasks: [
            {
                distinctId: 'task_0',
                subtasks: [],
            },
        ],
    });

    const supplierNpiList = useMemo(() => {
        const unsortedList = (supplierNpis ?? []).map((npi) => ({
            name: npi.organization_name,
            secondaryData: npi.supplier_npi,
            organization_name: npi.organization_name,
            supplier_npi: npi.supplier_npi,
        }));

        return unsortedList.sort(compareProp('name'));
    }, [supplierNpis]);

    const enrichedStratRules = useMemo(() => {
        if (stratRules && supplierNpiList.length > 0) {
            return stratRules.map((entry) => ({
                ...entry,
                configurable_params: setSupplierNpiOptions(
                    entry.configurable_params,
                    supplierNpiList,
                ),
            }));
        }
        return undefined;
    }, [supplierNpiList, stratRules]);

    const fetchValueCase = useCallback(async (id, callbackFunction = undefined) => {
        try {
            const newValues = await getValueCase(id);
            const newInitialValues = {
                ...newValues,
                incentive_amt: (newValues?.product_service_incentives ?? [])[0]?.incentive_amt,
            };
            setInitalValues(newInitialValues);
            if (callbackFunction) {
                callbackFunction(newInitialValues);
            }
        } catch (err) {
            handleError('Error fetching value case.', err);
        }
    }, []);

    const updateValueCase = async (id, valueCase) => {
        try {
            await putValueCase(id, valueCase);
            SuccessToast({ message: 'Successfully updated value case!' });
        } catch (err) {
            handleError('Error updating value case.', err);
        }
    };

    const submitValueCase = async (valueCase) => {
        try {
            await postValueCase(valueCase);
            SuccessToast({ message: 'Successfully created new value case!' });
        } catch (err) {
            handleError('Error saving value case.', err);
        }
    };

    useEffect(() => {
        const getStratRuleConfigurableParams = ({ strat_rule_id: stratRuleId }) =>
            (enrichedStratRules ?? []).filter((rule) => rule.strat_rule_id === stratRuleId)[0]
                ?.configurable_params;

        if (valueCaseId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            fetchValueCase(valueCaseId, (newInitialValues) => {
                setStratRuleConfigurableParams(getStratRuleConfigurableParams(newInitialValues));
            });
        }
    }, [fetchValueCase, valueCaseId, enrichedStratRules]);

    const handleDragEnd = (result, formik) => {
        const { destination, source } = result;
        if (!destination || !source) {
            return;
        }
        const newValues = structuredClone(formik.values);
        const newSourceArray = getIn(newValues, source.droppableId);
        const newDestinationArray = getIn(newValues, destination.droppableId);
        newDestinationArray.splice(destination.index, 0, ...newSourceArray.splice(source.index, 1));
        setIn(newValues, source.droppableId, newSourceArray);
        setIn(newValues, destination.droppableId, newDestinationArray);
        formik.setValues(newValues);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                if (valueCaseId) {
                    await updateValueCase(valueCaseId, values);
                } else {
                    await submitValueCase(values);
                }
                navigate({
                    pathname: '/value-cases',
                });
            }}
            validate={async (values) => {
                try {
                    await validateYupSchema(values, valueCaseValidationSchema, true, values);
                } catch (err) {
                    return yupToFormErrors(err);
                }
                return {};
            }}
            validateOnChange
            validateOnMount
            enableReinitialize
        >
            {(formik) => (
                <div className="sds_performance">
                    <UnsavedChangesPrompt
                        message="You have unsaved changes. Do you wish to continue?"
                        when={formik.dirty && !formik.isSubmitting}
                    />
                    <SelectExistingRequirementDialog
                        isOpen={!!openSelectExistingDialog}
                        onCancel={() => setOpenSelectExistingDialog()}
                        onSelect={(newPerfObject) => {
                            setOpenSelectExistingDialog();
                            setOpenSubTaskPreview();
                            formik.setValues({
                                ...formik.values,
                                ...newPerfObject,
                            });
                        }}
                        currentValueCaseId={valueCaseId}
                    />
                    <div className="sds_performance__column" style={{ width: '50%' }}>
                        <FormContainer
                            formTitle="Edit Value Case"
                            isLoading={valueCaseId && !initialValues?.value_case_id}
                            onFormSubmit={(e) => {
                                e.preventDefault();
                                formik.handleSubmit();
                            }}
                            isSubmitting={!(formik.dirty && formik.isValid)}
                            formLeftContent={
                                <>
                                    <ValueCaseDetails
                                        stratRules={enrichedStratRules ?? []}
                                        stratRuleConfigurableParams={stratRuleConfigurableParams}
                                        setStratRuleConfigurableParams={
                                            setStratRuleConfigurableParams
                                        }
                                        setOpenSelectExistingDialog={setOpenSelectExistingDialog}
                                    />
                                    <DragDropContext
                                        onDragEnd={(result) => handleDragEnd(result, formik)}
                                    >
                                        <DeleteItemDialog
                                            isOpen={!!openDeleteDialog}
                                            onCancel={() => setOpenDeleteDialog()}
                                            {...openDeleteDialog}
                                        />
                                        <DynamicCards
                                            id="tasks"
                                            arrayTitle="Performance Requirement Task(s)"
                                            elementTitleAttribute="task_name"
                                            elementName="task"
                                            relatedElementNames={['subtask', 'condition']}
                                            className="padding__top--medium"
                                            newElementCallback={() => ({
                                                distinctId: `task_${uuidv4()}`,
                                            })}
                                            elementComponents={[
                                                <TaskDetails key={0} />,
                                                <DynamicCards
                                                    key={1}
                                                    id="subtasks"
                                                    arrayTitle="Subtask(s)"
                                                    elementName="subtask"
                                                    relatedElementNames={['task', 'condition']}
                                                    elementTitleAttribute="subtask_title"
                                                    newElementCallback={() => ({
                                                        distinctId: `subtask_${uuidv4()}`,
                                                        activation_type: 'On Start',
                                                    })}
                                                    cardDepth={DynamicCards.cardDepths.MIDDLE}
                                                    elementComponents={[
                                                        <EditSubTaskDialog
                                                            key={0}
                                                            id={undefined}
                                                            openSubTaskDialog={
                                                                openEditSubTaskDialog
                                                            }
                                                            setOpenSubTaskDialog={
                                                                setOpenEditSubTaskDialog
                                                            }
                                                        />,
                                                        <DynamicCards
                                                            key={1}
                                                            id="conditions"
                                                            arrayTitle="Condition(s)"
                                                            elementName="condition"
                                                            relatedElementNames={[
                                                                'task',
                                                                'subtask',
                                                            ]}
                                                            elementTitleAttribute="condition_title"
                                                            cardDepth={
                                                                DynamicCards.cardDepths.BOTTOM
                                                            }
                                                            newElementCallback={() => ({
                                                                distinctId: `condition_${uuidv4()}`,
                                                            })}
                                                            elementComponents={[
                                                                <EditConditionDialog
                                                                    key={0}
                                                                    id={undefined}
                                                                    openConditionDialog={
                                                                        openEditConditionDialog
                                                                    }
                                                                    setOpenConditionDialog={(id) =>
                                                                        setOpenEditConditionDialog(
                                                                            id,
                                                                        )
                                                                    }
                                                                    conditionBlueprints={
                                                                        conditionBlueprints
                                                                    }
                                                                />,
                                                            ]}
                                                            editCallback={(id) =>
                                                                setOpenEditConditionDialog(id)
                                                            }
                                                            onCardDelete={({
                                                                cardPreview,
                                                                deleteCard,
                                                            }) => {
                                                                const onDelete = () => {
                                                                    setOpenDeleteDialog();
                                                                    deleteCard();
                                                                };
                                                                setOpenDeleteDialog({
                                                                    itemToDelete:
                                                                        DeleteItemDialog.Items
                                                                            .CONDITION,
                                                                    onDelete,
                                                                    itemPreview: cardPreview,
                                                                });
                                                            }}
                                                        />,
                                                    ]}
                                                    editCallback={(id) => {
                                                        setOpenEditSubTaskDialog(id);
                                                    }}
                                                    onCardClick={({ cardId }) => {
                                                        setOpenSubTaskPreview(cardId);
                                                    }}
                                                    cardSelected={openSubTaskPreview}
                                                    onCardDelete={({
                                                        cardId,
                                                        cardPreview,
                                                        deleteCard,
                                                    }) => {
                                                        const onDelete = () => {
                                                            if (openSubTaskPreview === cardId) {
                                                                setOpenSubTaskPreview();
                                                            }
                                                            setOpenDeleteDialog();
                                                            deleteCard();
                                                        };
                                                        setOpenDeleteDialog({
                                                            itemToDelete:
                                                                DeleteItemDialog.Items.SUBTASK,
                                                            onDelete,
                                                            itemPreview: cardPreview,
                                                        });
                                                    }}
                                                />,
                                            ]}
                                            onCardDelete={({ cardId, cardPreview, deleteCard }) => {
                                                const onDelete = () => {
                                                    if (openSubTaskPreview === cardId) {
                                                        setOpenSubTaskPreview();
                                                    }
                                                    setOpenDeleteDialog();
                                                    deleteCard();
                                                };
                                                setOpenDeleteDialog({
                                                    itemToDelete: DeleteItemDialog.Items.TASK,
                                                    onDelete,
                                                    itemPreview: cardPreview,
                                                });
                                            }}
                                        />
                                    </DragDropContext>
                                </>
                            }
                            onSecondaryButtonClick={() => {
                                navigate({
                                    pathname: '/value-cases',
                                });
                            }}
                        />
                    </div>
                    <div className="sds_performance__column" style={{ width: '50%' }}>
                        <FormSection
                            sectionTitle="Preview"
                            content={
                                <PerformancePreview
                                    subtaskElementId={openSubTaskPreview}
                                    formik={formik}
                                    showDefaultSubtaskPreview
                                    setOpenSubTaskPreview={setOpenSubTaskPreview}
                                />
                            }
                        />
                    </div>
                </div>
            )}
        </Formik>
    );
};
